import React, { useState } from 'react';
import { InteractionMode } from '../types';

interface TutorialProps {
  onClose: () => void;
  mode: InteractionMode;
  isMobile: boolean;
  onSelectMode?: (mode: InteractionMode) => void;
  onOpenMotionGuide?: () => void;
}

export const Tutorial: React.FC<TutorialProps> = ({ 
  onClose, 
  mode, 
  isMobile, 
  onSelectMode,
  onOpenMotionGuide 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeGuideMode, setActiveGuideMode] = useState<InteractionMode>(mode);

  const isCamera = activeGuideMode === InteractionMode.CAMERA;
  const isMotion = activeGuideMode === InteractionMode.MOTION;
  const isTouch = activeGuideMode === InteractionMode.TOUCH;

  const slides = [
    {
      title: isCamera 
        ? "Conduct Music with AI Webcam Tracking" 
        : (!isMobile ? "Conduct Music with Cursor & Motion" : (isMotion ? "Move Your Phone to Play" : "Touch Canvas to Sculpt Sound")),
      text: isCamera
        ? "Stand in front of your camera. MediaPipe AI tracks your hands & body movements in real-time. Elevate your right hand for melodic pitch, sweep your left hand for resonance, and spread your arms wide for ambient reverb."
        : (!isMobile 
          ? "Click anywhere on the screen to activate performance. Move your cursor across the visualizer to modulate pitch, resonance, and harmonic filters in real-time."
          : (isMotion 
            ? "Your device is your physical instrument! Tilt forward, backward, and side-to-side to shape synthesizers. Fast movements add rhythmic pulses and note triggers."
            : "Tap, slide, and drag your fingers across the visualizer canvas. Vertical motion changes pitch and octaves, while horizontal motion modulates acoustic resonance.")),
      visual: (
        <div className="relative w-36 h-28 flex items-center justify-center">
          {isCamera ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="absolute w-24 h-24 border border-sky-400/40 rounded-full animate-ping opacity-40"></div>
              <div className="z-10 bg-slate-900/90 p-3.5 rounded-2xl border border-sky-500/40 shadow-xl shadow-sky-950/60 flex flex-col items-center">
                <div className="flex items-center gap-2 mb-1 text-sky-300">
                  <span className="text-xl">🖐️</span>
                  <span className="text-xs font-black uppercase tracking-wider text-sky-400">AI Vision</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-300 font-bold bg-sky-500/20 px-2 py-0.5 rounded-md">
                  <span>↕️ Pitch</span>
                  <span>•</span>
                  <span>↔️ Filter</span>
                </div>
              </div>
            </div>
          ) : !isMobile ? (
            <div className="relative w-full h-full flex items-center justify-center">
               <div className="absolute w-20 h-20 border border-indigo-500/30 rounded-full animate-ping"></div>
               <div className="z-10 text-indigo-400 bg-slate-900/90 p-3 rounded-2xl border border-indigo-500/30 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 animate-bounce text-indigo-400">
                    <path d="M13.123 21.31c-.546.545-1.442.148-1.442-.625v-7.143L6.873 18.35c-.545.545-1.442.148-1.442-.625V4.625c0-.773.897-1.17 1.442-.625l11.41 11.411c.545.545.148 1.442-.625 1.442h-4.535l1.41 1.41c.545.545.148 1.442-.625 1.442l-.785-.785Z" />
                  </svg>
               </div>
            </div>
          ) : (
            <div className="w-16 h-24 border-2 border-indigo-400 rounded-2xl flex items-center justify-center animate-tilt-phone bg-slate-800/80 backdrop-blur-sm relative shadow-lg shadow-indigo-500/20">
               <div className="absolute top-2 w-4 h-1 bg-indigo-400/70 rounded-full"></div>
               <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center text-[10px] text-indigo-200 font-bold">♪</div>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Camera Mode: Physical Gesture Controls",
      text: "In Camera Mode, your physical body coordinates directly map to the synthesizer sound engine. Stand 5–7 feet from your webcam for best tracking.",
      visual: (
        <div className="grid grid-cols-2 gap-2 w-full max-w-xs text-left">
          <div className="p-2.5 rounded-xl bg-slate-900/90 border border-sky-500/30">
            <div className="text-[11px] font-bold text-sky-300 flex items-center gap-1">
              <span>🖐️</span>
              <span>Right Hand</span>
            </div>
            <div className="text-[10px] text-slate-400">Vertical height = Note Pitch</div>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-900/90 border border-teal-500/30">
            <div className="text-[11px] font-bold text-teal-300 flex items-center gap-1">
              <span>🖐️</span>
              <span>Left Hand</span>
            </div>
            <div className="text-[10px] text-slate-400">Vertical height = Filter Brightness</div>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-900/90 border border-indigo-500/30">
            <div className="text-[11px] font-bold text-indigo-300 flex items-center gap-1">
              <span>↔️</span>
              <span>Arm Spread</span>
            </div>
            <div className="text-[10px] text-slate-400">Distance = Stereo Reverb Depth</div>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-900/90 border border-amber-500/30">
            <div className="text-[11px] font-bold text-amber-300 flex items-center gap-1">
              <span>⚡</span>
              <span>Speed & Velocity</span>
            </div>
            <div className="text-[10px] text-slate-400">Fast sweeps = Dynamic Accents</div>
          </div>
        </div>
      )
    },
    {
      title: "Explore Sound Library & Instant Audition",
      text: "Open the Sound Library at the bottom right to browse curated synthesizer presets across Ambient, Healing, Chakra, Electronic, and Space soundscapes. Tap 'Audition' to preview sounds instantly without interrupting your session.",
      visual: (
        <div className="flex items-center gap-2 p-3 bg-slate-900/90 rounded-2xl border border-indigo-500/30 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-xs font-bold text-white">Preset Library</div>
            <div className="text-[10px] text-indigo-300">Audition & Perform with 1-Tap</div>
          </div>
        </div>
      )
    },
    {
      title: "Capture Live Sessions & Performance Metrics",
      text: "Tap the Red Record Button while performing to record your session. The app captures your audio alongside live movement telemetry, measuring gesture fluidity, tempo stability, and physical intensity.",
      visual: (
        <div className="flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center animate-pulse shadow-lg shadow-red-500/30">
            <div className="w-5 h-5 rounded-full bg-red-500"></div>
          </div>
        </div>
      )
    },
    {
      title: "AI Mindfulness Reports & Flow Score",
      text: "Inside the Recordings tab, tap 'Analyze Mindfulness Report' on any session. The AI analyzes your real movement telemetry to calculate your Flow State Score, detailed rhythm breakdowns, and personalized Mind & Body reflections.",
      visual: (
        <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-teal-950/60 to-indigo-950/60 rounded-2xl border border-teal-500/30 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-300 font-bold text-sm">
            92%
          </div>
          <div className="text-left">
            <div className="text-xs font-bold text-white">Flow Mastery Report</div>
            <div className="text-[10px] text-teal-300">Session-specific reflections</div>
          </div>
        </div>
      )
    },
    {
      title: "Dynamic Guided Breathing & Ambience",
      text: "Every Mindfulness Report automatically highlights the breathing exercise best matched to your session's physical intensity (4-7-8, Box Breathing, 5-5 Coherent, or Restorative) with ocean, rain, or mountain soundscapes.",
      visual: (
        <div className="flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-teal-500/20 border-2 border-teal-400 flex items-center justify-center animate-pulse text-teal-200 text-xs font-bold shadow-lg shadow-teal-500/20">
            Breathe
          </div>
        </div>
      )
    }
  ];

  return (
    <div 
      id="user-guide-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-modal-title"
      className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-fade-in"
    >
      <div className="bg-slate-900 border border-white/10 w-full max-w-lg rounded-[2.5rem] p-6 sm:p-8 shadow-2xl overflow-hidden flex flex-col items-center text-center relative">
        <button 
          id="close-guide-modal-btn"
          onClick={onClose}
          className="absolute top-6 right-6 text-slate-500 hover:text-white p-2 rounded-full hover:bg-white/5 transition cursor-pointer"
          aria-label="Close Guide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="w-12 h-1 bg-white/20 rounded-full mb-4 sm:hidden"></div>

        {/* Mode Selector Pill inside Guide */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-950/80 rounded-2xl border border-white/10 mb-5 overflow-x-auto max-w-full">
          <button
            type="button"
            onClick={() => {
              setActiveGuideMode(InteractionMode.CAMERA);
              if (onSelectMode) onSelectMode(InteractionMode.CAMERA);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              isCamera ? 'bg-sky-500 text-white shadow-md shadow-sky-500/20' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span>🖐️</span>
            <span>Webcam Mode</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveGuideMode(InteractionMode.MOTION);
              if (onSelectMode) onSelectMode(InteractionMode.MOTION);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              isMotion ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span>{isMobile ? '📱' : '🖱️'}</span>
            <span>{isMobile ? 'Phone Tilt' : 'Cursor Motion'}</span>
          </button>

          {isMobile && (
            <button
              type="button"
              onClick={() => {
                setActiveGuideMode(InteractionMode.TOUCH);
                if (onSelectMode) onSelectMode(InteractionMode.TOUCH);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                isTouch ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <span>👆</span>
              <span>Touch</span>
            </button>
          )}
        </div>

        <div className="mb-5 flex items-center justify-center min-h-[100px] w-full">{slides[currentSlide].visual}</div>

        <h3 id="guide-modal-title" className="text-lg sm:text-2xl font-black text-white uppercase italic tracking-tight mb-2">
          {slides[currentSlide].title}
        </h3>

        <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-5 min-h-[50px]">
          {slides[currentSlide].text}
        </p>

        {/* Step Indicators */}
        <div className="flex gap-2 mb-6">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                currentSlide === i ? 'w-8 bg-indigo-500' : 'w-2 bg-slate-700 hover:bg-slate-500'
              }`}
            />
          ))}
        </div>

        <div className="flex gap-3 w-full">
          {currentSlide > 0 && (
            <button 
              id="guide-back-btn"
              onClick={() => setCurrentSlide(currentSlide - 1)} 
              className="flex-1 py-3.5 rounded-2xl font-black uppercase text-xs text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition cursor-pointer"
            >
              Back
            </button>
          )}
          <button 
            id="guide-next-btn"
            onClick={() => currentSlide < slides.length - 1 ? setCurrentSlide(currentSlide + 1) : onClose()} 
            className="flex-1 py-3.5 rounded-2xl font-black uppercase text-xs text-white bg-indigo-600 hover:bg-indigo-500 transition shadow-xl shadow-indigo-600/30 cursor-pointer active:scale-95 flex items-center justify-center gap-2"
          >
            <span>{currentSlide === slides.length - 1 ? "Start Performing" : "Next"}</span>
            {currentSlide < slides.length - 1 && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>

        {onOpenMotionGuide && (
          <button
            onClick={() => {
              onClose();
              onOpenMotionGuide();
            }}
            className="mt-3 text-[11px] text-sky-400 hover:text-sky-300 underline underline-offset-4 cursor-pointer"
          >
            Open Complete Interactive Gesture Mapping Guide →
          </button>
        )}
      </div>
    </div>
  );
};
